/**
 * localStorage 管理工具
 */
const StorageKeys = {
  USER: 'user',
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  LOCALE: 'locale',
  THEME: 'theme',
}

/**
 * 获取localStorage值
 * @param {string} key - 存储键
 * @param {*} defaultValue - 默认值
 * @returns {*} 存储的值或默认值
 */
export function getStorageItem(key, defaultValue = null) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultValue
  }
  try {
    const item = localStorage.getItem(key)
    if (item === null || item === undefined) {
      return defaultValue
    }
    
    // 尝试解析JSON，如果失败则返回原始字符串
    try {
      return JSON.parse(item)
    } catch (e) {
      // 如果解析失败，说明存储的是普通字符串，直接返回
      return item
    }
  } catch (err) {
    console.error(`Error getting storage item ${key}:`, err)
    return defaultValue
  }
}

/**
 * 设置localStorage值
 * @param {string} key - 存储键
 * @param {*} value - 要存储的值
 */
export function setStorageItem(key, value) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    // 如果值已经是字符串，直接存储；否则转换为JSON字符串
    if (typeof value === 'string') {
      localStorage.setItem(key, value)
    } else if (value === null || value === undefined) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(value))
    }
  } catch (e) {
    console.error(`Error setting storage item ${key}:`, e)
  }
}

/**
 * 删除localStorage值
 * @param {string} key - 存储键
 */
export function removeStorageItem(key) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    localStorage.removeItem(key)
  } catch (e) {
    console.error(`Error removing storage item ${key}:`, e)
  }
}

/**
 * 清空localStorage
 */
export function clearStorage() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    localStorage.clear()
  } catch (e) {
    console.error('Error clearing storage:', e)
  }
}

/**
 * 获取用户ID
 * @returns {string|null}
 */
export function getUserId() {
  return getStorageItem(StorageKeys.USER, null)
}

/**
 * 设置用户ID
 * @param {string} userId
 */
export function setUserId(userId) {
  setStorageItem(StorageKeys.USER, userId)
}

/**
 * 获取访问令牌
 * @returns {string|null}
 */
export function getAccessToken() {
  return getStorageItem(StorageKeys.ACCESS_TOKEN, null)
}

/**
 * 设置访问令牌
 * @param {string} token
 */
export function setAccessToken(token) {
  setStorageItem(StorageKeys.ACCESS_TOKEN, token)
}

/**
 * 获取刷新令牌
 * @returns {string|null}
 */
export function getRefreshToken() {
  return getStorageItem(StorageKeys.REFRESH_TOKEN, null)
}

/**
 * 设置刷新令牌
 * @param {string} token
 */
export function setRefreshToken(token) {
  setStorageItem(StorageKeys.REFRESH_TOKEN, token)
}

/**
 * 清除用户认证信息
 */
export function clearAuth() {
  removeStorageItem(StorageKeys.USER)
  removeStorageItem(StorageKeys.ACCESS_TOKEN)
  removeStorageItem(StorageKeys.REFRESH_TOKEN)
}

/**
 * 检查用户是否已登录
 * @returns {boolean}
 */
export function isAuthenticated() {
  const userId = getUserId()
  const accessToken = getAccessToken()
  return !!(userId && accessToken)
}

export { StorageKeys }

